import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg } from "@/lib/api";
import { DESTINATIONS } from "@/lib/movements";

const sel = "h-10 w-full rounded-md border border-border bg-transparent px-2 text-sm";
const blank = () => ({ origin_line_id: "", type_name: "", brand: "", model: "", serial: "", destination: "cliente", custodian: "", custody_date: "", notes: "", new_model: "", new_serial: "" });

const Rep = ({ r, i, lines, onChange, onRemove }) => {
  const set = (k, v) => onChange({ ...r, [k]: v });
  return (
    <div className="space-y-2 rounded-lg border border-border p-3" data-testid={`bis-rep-${i}`}>
      <div className="flex gap-2">
        <select className={sel} value={r.origin_line_id} onChange={(e) => set("origin_line_id", e.target.value)} data-testid={`bis-rep-${i}-line`}>
          <option value="">Equipo no listado en la OS origen (capturar)</option>
          {lines.map((l) => <option key={l.line_id} value={l.line_id}>{[l.type_name, l.brand, l.model].filter(Boolean).join(" · ")}</option>)}
        </select>
        <Button size="icon" variant="ghost" onClick={onRemove} data-testid={`bis-rep-${i}-remove`}><Trash2 className="h-4 w-4" /></Button>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {!r.origin_line_id && <><Input placeholder="Tipo *" value={r.type_name} onChange={(e) => set("type_name", e.target.value)} data-testid={`bis-rep-${i}-type`} /><Input placeholder="Marca" value={r.brand} onChange={(e) => set("brand", e.target.value)} /><Input placeholder="Modelo" value={r.model} onChange={(e) => set("model", e.target.value)} /></>}
        <Input placeholder="Serie (si existe)" value={r.serial} onChange={(e) => set("serial", e.target.value)} data-testid={`bis-rep-${i}-serial`} />
        <select className={sel} value={r.destination} onChange={(e) => set("destination", e.target.value)} data-testid={`bis-rep-${i}-destination`}>
          {DESTINATIONS.map(([k, l]) => <option key={k} value={k}>Destino: {l}</option>)}
        </select>
        {r.destination === "tssmx" && <><Input placeholder="Responsable *" value={r.custodian} onChange={(e) => set("custodian", e.target.value)} data-testid={`bis-rep-${i}-custodian`} /><Input type="date" value={r.custody_date} onChange={(e) => set("custody_date", e.target.value)} data-testid={`bis-rep-${i}-custody-date`} /></>}
        <Input placeholder="Modelo nuevo" value={r.new_model} onChange={(e) => set("new_model", e.target.value)} data-testid={`bis-rep-${i}-new-model`} />
        <Input placeholder="Serie nueva" value={r.new_serial} onChange={(e) => set("new_serial", e.target.value)} data-testid={`bis-rep-${i}-new-serial`} />
      </div>
    </div>
  );
};

export const BisDialog = ({ wo, open, onOpenChange }) => {
  const nav = useNavigate();
  const [kind, setKind] = useState("ampliacion");
  const [scope, setScope] = useState("");
  const [reps, setReps] = useState([]);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const body = { kind, scope, replacements: kind === "reemplazo" ? reps.map((r) => ({ ...r, origin_line_id: r.origin_line_id || null, custody_date: r.custody_date || null })) : [] };
      const { data } = await api.post(`/work-orders/${wo.id}/bis`, body);
      toast.success(`BIS ${data.folio} creado`);
      onOpenChange(false);
      nav(`/admin/os/${data.id}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="bis-dialog">
        <DialogHeader><DialogTitle className="font-display">Nuevo BIS de {wo.folio}</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Hereda solo cliente, ubicación y OS origen. Ejecución, evidencia, resultado y firma independientes.</p>
        <div className="space-y-3">
          <div className="space-y-1"><Label>Tipo</Label>
            <select className={sel} value={kind} onChange={(e) => { setKind(e.target.value); if (e.target.value === "reemplazo" && !reps.length) setReps([blank()]); }} data-testid="bis-kind-select">
              <option value="ampliacion">Ampliación (solo equipos nuevos)</option><option value="reemplazo">Reemplazo de equipo</option>
            </select>
          </div>
          <div className="space-y-1"><Label>Alcance *</Label><Textarea rows={3} value={scope} onChange={(e) => setScope(e.target.value)} data-testid="bis-scope-input" /></div>
          {kind === "reemplazo" && (
            <div className="space-y-2">
              <Label>Equipo a sustituir</Label>
              {reps.map((r, i) => <Rep key={i} r={r} i={i} lines={wo.equipment || []} onChange={(nr) => setReps(reps.map((x, j) => (j === i ? nr : x)))} onRemove={() => setReps(reps.filter((_, j) => j !== i))} />)}
              <Button size="sm" variant="outline" onClick={() => setReps([...reps, blank()])} data-testid="bis-add-rep"><Plus className="mr-1 h-4 w-4" />Equipo</Button>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={submit} disabled={busy || scope.trim().length < 3} data-testid="bis-submit-button">Crear BIS</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
